import { useState, useEffect } from 'react';
import apiService from '../../utils/api';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { useToast } from '../../components/Toast';
import { sortedByName } from '../../utils/sortAlpha';
import { Plus, Edit, Trash2, X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
const TODAY_DATE = new Date().toISOString().split('T')[0];

const AnimalPurchase = () => {
  const toast = useToast();
  const [purchases, setPurchases] = useState([]);
  const [khatas, setKhatas] = useState([]);
  const [tabelas, setTabelas] = useState([]);
  const [animalVendors, setAnimalVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [filters, setFilters] = useState({ start_date: '', end_date: '', bought_from: '' });

  const emptyForm = {
    date: new Date().toISOString().split('T')[0],
    vendor_id: '',
    bought_from: '',
    type: '',
    breed: '',
    lactation: '',
    price: '',
    tag_number: '',
    tabela_id: '',
    khata_id: '',
    notes: '',
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadPurchases();
    loadKhatasTabelas();
  }, [currentPage, filters]);

  const loadPurchases = async () => {
    try {
      setLoading(true);
      const res = await apiService.getAnimalPurchases({ page: currentPage, per_page: 20, ...filters });
      const d = res.data || res;
      setPurchases(d.data || []);
      setTotalPages(d.pagination?.pages || 1);
      setTotalRecords(d.pagination?.total || 0);
      setError(null);
    } catch (err) {
      setError('Failed to load animal purchases');
    } finally {
      setLoading(false);
    }
  };

  const loadKhatasTabelas = async () => {
    try {
      const [kr, tr, vr] = await Promise.all([
        apiService.getKhata(),
        apiService.getTabela(),
        apiService.getVendors({ vendor_type: 'animal' })
      ]);
      setKhatas(kr.data || []);
      setTabelas(tr.data || []);
      setAnimalVendors(vr.data || []);
    } catch (_) {}
  };

  const getFilteredKhatas = () => {
    if (!formData.tabela_id) return [];
    return khatas.filter(k => k.tabela_id === parseInt(formData.tabela_id));
  };

  const getKhataNo = (khataId) => {
    const k = khatas.find(k => k.khata_id === khataId);
    return k ? k.khata_no : '—';
  };

  const getTabelaName = (khataId) => {
    const k = khatas.find(k => k.khata_id === khataId);
    if (!k) return '—';
    const t = tabelas.find(t => t.tabela_id === k.tabela_id);
    return t ? t.name : '—';
  };

  const openAdd = () => {
    setEditingPurchase(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditingPurchase(p);
    // Find tabela_id from khata
    const k = khatas.find(k => k.khata_id === p.khata_id);
    setFormData({
      date: p.date || emptyForm.date,
      vendor_id: p.vendor_id ? p.vendor_id.toString() : '',
      bought_from: p.bought_from || '',
      type: p.type || '',
      breed: p.breed || '',
      lactation: p.lactation || '',
      price: p.price || '',
      tag_number: p.tag_number || '',
      tabela_id: k ? k.tabela_id.toString() : '',
      khata_id: p.khata_id ? p.khata_id.toString() : '',
      notes: p.notes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this animal purchase record?')) return;
    try {
      await apiService.deleteAnimalPurchase(id);
      await loadPurchases();
    } catch (err) {
      toast.error('Error deleting: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vendor_id) { toast.warning('Please select an animal vendor'); return; }
    if (!formData.type) { toast.warning('Type (Cow/Buffalo) is required'); return; }
    if (!formData.price || isNaN(parseFloat(formData.price))) { toast.warning('Valid price is required'); return; }
    if (!formData.date) { toast.warning('Date is required'); return; }

    const payload = {
      date: formData.date,
      vendor_id: parseInt(formData.vendor_id),
      bought_from: formData.bought_from || null,
      type: formData.type,
      breed: formData.breed || null,
      lactation: formData.lactation ? parseInt(formData.lactation) : null,
      price: parseFloat(formData.price),
      tag_number: formData.tag_number ? formData.tag_number.toUpperCase() : null,
      khata_id: formData.khata_id ? parseInt(formData.khata_id) : null,
      notes: formData.notes || null,
    };

    try {
      setLoading(true);
      if (editingPurchase) {
        await apiService.updateAnimalPurchase(editingPurchase.purchase_id, payload);
      } else {
        await apiService.createAnimalPurchase(payload);
      }
      setShowForm(false);
      setFormData(emptyForm);
      setEditingPurchase(null);
      await loadPurchases();
    } catch (err) {
      toast.error('Error saving: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tabela_id') {
      setFormData({ ...formData, tabela_id: value, khata_id: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
  const clearFilters = () => { setFilters({ start_date: '', end_date: '', bought_from: '' }); setCurrentPage(1); };

  return (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">

          <div className="page-header">
            <h1 className="page-title">Animal Purchases</h1>
            <button className="btn btn-primary" onClick={openAdd}>
              <Plus size={18} /> Add Purchase
            </button>
          </div>

          {error && <div className="alert alert-error"><p>{error}</p></div>}

          {/* Filters */}
          <div className="filter-bar">
            <div className="filter-group">
              <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>From Date</label>
              <input type="date" max={TODAY_DATE} name="start_date" className="form-input" value={filters.start_date} onChange={handleFilterChange} />
            </div>
            <div className="filter-group">
              <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>To Date</label>
              <input type="date" max={TODAY_DATE} name="end_date" className="form-input" value={filters.end_date} onChange={handleFilterChange} />
            </div>
            <div className="filter-group">
              <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Bought From</label>
              <input type="text" name="bought_from" className="form-input" value={filters.bought_from} onChange={handleFilterChange} placeholder="Search seller" />
            </div>
            <button className="btn btn-secondary" onClick={clearFilters} style={{ height: '42px', alignSelf: 'flex-end' }}>Clear</button>
          </div>

          {/* Table */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vendor</th>
                  <th>Bought From</th>
                  <th>Type</th>
                  <th>Breed</th>
                  <th>Lactation</th>
                  <th>Tag No</th>
                  <th>Tabela</th>
                  <th>Khata</th>
                  <th>Price (₹)</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map(p => (
                  <tr key={p.purchase_id}>
                    <td>{p.date ? new Date(p.date).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={{ fontWeight: 500 }}>{p.vendor_name || '—'}</td>
                    <td>{p.bought_from || '—'}</td>
                    <td>{p.type}</td>
                    <td>{p.breed || '—'}</td>
                    <td>{p.lactation ?? '—'}</td>
                    <td>{p.tag_number || '—'}</td>
                    <td>{getTabelaName(p.khata_id)}</td>
                    <td>{getKhataNo(p.khata_id)}</td>
                    <td style={{ fontWeight: 600 }}>₹{p.price?.toLocaleString('en-IN')}</td>
                    <td style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.notes || '—'}</td>
                    <td>
                      <div className="actions-cell">
                        <button className="btn-icon" onClick={() => openEdit(p)} title="Edit"><Edit size={18} /></button>
                        <button className="btn-icon" style={{ color: 'var(--error-color)' }} onClick={() => handleDelete(p.purchase_id)} title="Delete"><Trash2 size={18} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {purchases.length === 0 && !loading && (
              <div className="no-data"><p>No animal purchases found</p></div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button className="pagination-btn" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}><ChevronLeft size={16} /></button>
              {[...Array(totalPages)].map((_, i) => (
                <button key={i + 1} className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
              ))}
              <button className="pagination-btn" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}><ChevronRight size={16} /></button>
            </div>
          )}
          <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Showing {purchases.length} of {totalRecords} records
          </div>

          {/* Form Modal */}
          {showForm && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div className="card" style={{ width: '100%', maxWidth: '620px', margin: '1rem', maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="card-title">{editingPurchase ? 'Edit Animal Purchase' : 'Add Animal Purchase'}</h3>
                  <button className="btn-icon" onClick={() => setShowForm(false)}><X size={20} /></button>
                </div>

                <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Date *</label>
                      <input type="date" max={TODAY_DATE} name="date" className="form-input" value={formData.date} onChange={handleInputChange} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Vendor *</label>
                      <select name="vendor_id" className="form-select" value={formData.vendor_id} onChange={handleInputChange} required>
                        <option value="">Select Animal Vendor</option>
                        {sortedByName(animalVendors).map(v => (
                          <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>
                        ))}
                      </select>
                      {animalVendors.length === 0 && (
                        <small style={{ color: 'var(--error-color)' }}>
                          No animal vendors found. Add one in Vendors (Portfolio) with type = Animal.
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Bought From (farmer name, optional)</label>
                    <input type="text" name="bought_from" className="form-input" value={formData.bought_from} onChange={handleInputChange} placeholder="Original farmer's name (if any)" />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Type *</label>
                      <select name="type" className="form-select" value={formData.type} onChange={handleInputChange} required>
                        <option value="">Select Type</option>
                        <option value="Cow">Cow</option>
                        <option value="Buffalo">Buffalo</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Breed</label>
                      <input type="text" name="breed" className="form-input" value={formData.breed} onChange={handleInputChange} placeholder="e.g. HF, Murrah" />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Lactation No.</label>
                      <input type="number" name="lactation" className="form-input" value={formData.lactation} onChange={handleInputChange} placeholder="e.g. 2" min="0" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Price (₹) *</label>
                      <input type="number" name="price" className="form-input" value={formData.price} onChange={handleInputChange} placeholder="Purchase price" min="0" required />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Tag Number (assigned to animal)</label>
                    <input type="text" name="tag_number" className="form-input" value={formData.tag_number} onChange={handleInputChange} placeholder="e.g. B205" disabled={!!editingPurchase} />
                    {!editingPurchase && <small style={{ color: 'var(--text-secondary)' }}>This tag will be auto-assigned to the new animal in portfolio</small>}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Tabela</label>
                      <select name="tabela_id" className="form-select" value={formData.tabela_id} onChange={handleInputChange}>
                        <option value="">Select Tabela</option>
                        {sortedByName(tabelas).map(t => <option key={t.tabela_id} value={t.tabela_id}>{t.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Khata</label>
                      <select name="khata_id" className="form-select" value={formData.khata_id} onChange={handleInputChange} disabled={!formData.tabela_id}>
                        <option value="">Select Khata</option>
                        {sortedByName(getFilteredKhatas(), k => k.khata_no).map(k => <option key={k.khata_id} value={k.khata_id}>{k.khata_no}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <textarea name="notes" className="form-input" value={formData.notes} onChange={handleInputChange} placeholder="Any additional notes" rows={2} style={{ resize: 'vertical' }} />
                  </div>

                  <div className="form-actions" style={{ marginTop: '1rem' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={loading}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      <Check size={18} />
                      {loading ? 'Saving...' : (editingPurchase ? 'Update Purchase' : 'Add Purchase')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AnimalPurchase;
